import { Typography } from "@/components/common/Typography/Typography"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { X } from "lucide-react"
import { nanoid } from "nanoid"
import { type ChangeEvent, type KeyboardEvent, useRef, useState } from "react"

export interface TagInputTag {
  id: string
  text: string
}

interface TagInputProps {
  id?: string
  tags: TagInputTag[]
  onTagsChange: (tags: TagInputTag[]) => void
  placeholder?: string
  disabled?: boolean
  maxTags?: number
  minLength?: number
  maxLength?: number
  /** Extra rule for the trimmed text; invalid text is not added. */
  validate?: (text: string) => boolean
}

/**
 * Inline tag field: Enter, a comma or leaving the field adds the text as a
 * tag (a pasted "a, b, c" adds three); the × button, or ←/→ then Backspace
 * on an empty field, removes one. Duplicates are skipped.
 */
export function TagInput({
  id,
  tags,
  onTagsChange,
  placeholder,
  disabled = false,
  maxTags,
  minLength,
  maxLength,
  validate,
}: TagInputProps) {
  const { translate } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [draft, setDraft] = useState("")
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  // Derived rather than synced, so a removal or a reset from the parent can
  // never leave a stale highlight behind.
  const active = activeIndex !== null && tags[activeIndex] ? activeIndex : null
  const isFull = maxTags !== undefined && tags.length >= maxTags

  const isValid = (text: string) =>
    text !== "" &&
    (minLength === undefined || text.length >= minLength) &&
    (maxLength === undefined || text.length <= maxLength) &&
    (validate?.(text) ?? true)

  // Duplicates and maxTags are checked per item, so a pasted list can't
  // overshoot the limit or add the same term twice.
  const addTags = (texts: string[]) => {
    const next = [...tags]
    for (const text of texts.map((value) => value.trim())) {
      if (maxTags !== undefined && next.length >= maxTags) break
      if (isValid(text) && !next.some((tag) => tag.text === text)) {
        next.push({ id: nanoid(), text })
      }
    }
    if (next.length > tags.length) onTagsChange(next)
  }

  // Enter and blur keep invalid text so it can be fixed; a duplicate is
  // dropped.
  const commitDraft = () => {
    const text = draft.trim()
    if (!isValid(text)) return
    addTags([text])
    setDraft("")
  }

  const removeTag = (tagId: string) =>
    onTagsChange(tags.filter((tag) => tag.id !== tagId))

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setActiveIndex(null)
    if (!value.includes(",")) {
      setDraft(value)
      return
    }
    addTags(value.split(","))
    setDraft("")
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      // Enter that confirms an IME composition is not a commit.
      if (event.nativeEvent.isComposing) return
      event.preventDefault()
      commitDraft()
      return
    }
    // While there is text, the arrow and delete keys belong to the caret.
    if (draft !== "" || tags.length === 0) return
    const last = tags.length - 1
    let next: number
    switch (event.key) {
      case "ArrowLeft":
        next = active === null || active === 0 ? last : active - 1
        break
      case "ArrowRight":
        next = active === null || active === last ? 0 : active + 1
        break
      case "Home":
        next = 0
        break
      case "End":
        next = last
        break
      case "Backspace":
      case "Delete":
        if (active === null) return
        removeTag(tags[active].id)
        next =
          event.key === "Backspace" ? active - 1 : Math.min(active, last - 1)
        break
      default:
        return
    }
    event.preventDefault()
    setActiveIndex(next)
  }

  return (
    <div
      className="flex min-h-10 w-full flex-row flex-wrap items-center gap-2 rounded-md border border-input bg-transparent px-3 py-2 shadow-xs focus-within:ring-1 focus-within:ring-ring"
      onClick={() => inputRef.current?.focus()}
    >
      {tags.map((tag, index) => (
        <span
          key={tag.id}
          className={cn(
            "inline-flex h-8 items-center rounded-sm border bg-secondary pl-2 transition-all hover:bg-secondary/80",
            index === active &&
              "ring-2 ring-ring ring-offset-2 ring-offset-background",
          )}
        >
          <Typography as="span" variant="textS">
            {tag.text}
          </Typography>
          <Button
            type="button"
            variant="ghost"
            disabled={disabled}
            className="h-full px-3 py-1 hover:bg-transparent [&_svg]:size-3.5"
            aria-label={translate("TAG_INPUT/REMOVE_TAG_ARIA", {
              tag: tag.text,
            })}
            onClick={(event) => {
              event.stopPropagation()
              removeTag(tag.id)
            }}
          >
            <X />
          </Button>
        </span>
      ))}
      <Input
        ref={inputRef}
        id={id}
        value={draft}
        autoComplete="off"
        disabled={disabled || isFull}
        placeholder={
          isFull
            ? translate("TAG_INPUT/MAX_REACHED", { max: maxTags })
            : placeholder
        }
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setActiveIndex(null)}
        onBlur={() => {
          setActiveIndex(null)
          commitDraft()
        }}
        className="h-auto w-fit min-w-32 flex-1 border-0 px-0 py-0 text-sm shadow-none focus-visible:ring-0 md:text-sm"
      />
    </div>
  )
}
